import { useContext } from "react";
import { useIntl } from "react-intl";

import { useMutation } from "@tanstack/react-query";

import { Inputs } from "../../../api/inputs";
import { Plants } from "../../../api/plants";
import { CafsType } from "../../../consts/cafsType";
import { MutationKey } from "../../../consts/mutation-keys";
import { QueryKey } from "../../../consts/query-keys";
import { useApiError } from "../../../hooks/useApiError";
import { useApiSuccess } from "../../../hooks/useApiSuccess";
import { useFetchError } from "../../../hooks/useFetchError";
import { ApiError } from "../../../types/api-error";
import { ApiHookConfig } from "../../../types/api-hook-config";
import { CafsTreeNodeType } from "../../../types/cafs-tree-node-type";
import { DashboardContext } from "../DashboardContext";

type UseCafsUnassignProps = ApiHookConfig & {
  cafs: CafsTreeNodeType | undefined;
};

export const useCafsUnassign = ({ onSuccess, cafs }: UseCafsUnassignProps) => {
  const { selectedDataset } = useContext(DashboardContext);
  const { formatMessage } = useIntl();

  const { handleSuccess } = useApiSuccess({
    snackbarText: formatMessage(
      {
        id: "ACTION.CAFS_UNASSIGN_SUCCESS",
        defaultMessage:
          "Item {name} has been removed from {dataset} successfully.",
        description: "Success message for CAFS removal from dataset",
      },
      {
        name: cafs?.name,
        dataset: selectedDataset?.Name,
      },
    ),
    invalidate: [[QueryKey.DataSetDetails]],
    onSuccessFn: onSuccess,
  });

  const { handleError } = useApiError();

  const mutationFn = () => {
    return cafs?.type === CafsType.Input
      ? Inputs.datasetUnassign({
          path: { datasetId: +selectedDataset?.Id!, inputId: +cafs?.id! },
        })
      : Plants.datasetUnassign({
          path: { datasetId: +selectedDataset?.Id!, plantId: +cafs?.id! },
        });
  };

  const {
    mutate: unassignCafs,
    isPending: unassignCafsPending,
    error,
  } = useMutation({
    mutationKey: [MutationKey.DatasetUnassign, +selectedDataset!],
    mutationFn: mutationFn,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  useFetchError(error as unknown as ApiError);

  return { unassignCafs, unassignCafsPending };
};
